import styled from '@emotion/styled';
import { Shield, Trophy } from 'lucide-react';
import { PlaceholderKey } from 'modules/manager/domain/placeholder/placeholder-keys';
import React from 'react';
import { Decor, Hot, Logo, Poster, PosterHeader, Subtitle, Title, Titles, useKeyText } from './scheme-parts';

/** One match: SCHEDULE and RESULTS. Score and goal scorers are drawn only where the type allows them. */
const MatchScheme: React.FC = () => {
  const text = useKeyText();
  return (
    <Poster>
      <PosterHeader>
        <Hot k={PlaceholderKey.TOURNAMENT_LOGO}>
          <Logo>
            <Trophy strokeWidth={1.5} />
          </Logo>
        </Hot>
        <Titles>
          <Hot k={PlaceholderKey.CHAMPIONSHIP_NAME}>
            <Title>{text(PlaceholderKey.CHAMPIONSHIP_NAME)}</Title>
          </Hot>
          <Hot k={PlaceholderKey.TOURNAMENT_NAME}>
            <Subtitle>{text(PlaceholderKey.TOURNAMENT_NAME)}</Subtitle>
          </Hot>
        </Titles>
      </PosterHeader>
      <Center>
        <Hot k={PlaceholderKey.ROUND_NAME}>{text(PlaceholderKey.ROUND_NAME)}</Hot>
      </Center>
      <Match>
        <Team>
          <Hot k={PlaceholderKey.TEAM_LOGO}>
            <Logo round>
              <Shield strokeWidth={1.5} />
            </Logo>
          </Hot>
          <Hot k={PlaceholderKey.TEAM_NAME}>
            <TeamName>{text(PlaceholderKey.TEAM_NAME)}</TeamName>
          </Hot>
          <Hot k={PlaceholderKey.TEAM_SHORT_NAME}>{text(PlaceholderKey.TEAM_SHORT_NAME)}</Hot>
          <Hot k={PlaceholderKey.SCORE_TEAM}>{text(PlaceholderKey.SCORE_TEAM)}</Hot>
        </Team>
        <Hot
          k={PlaceholderKey.SCORE}
          fallback={
            <Decor>
              <Score>—</Score>
            </Decor>
          }
        >
          <Score>{text(PlaceholderKey.SCORE)}</Score>
        </Hot>
        <Team>
          <Logo round>
            <Shield strokeWidth={1.5} />
          </Logo>
          <Decor>
            <TeamName>{text(PlaceholderKey.TEAM_NAME, 1)}</TeamName>
          </Decor>
          <Decor>{text(PlaceholderKey.TEAM_SHORT_NAME, 1)}</Decor>
        </Team>
      </Match>
      <Goals>
        <Hot k={PlaceholderKey.GOAL_SCORERS}>{text(PlaceholderKey.GOAL_SCORERS)}</Hot>
      </Goals>
      <Center>
        <Hot k={PlaceholderKey.MATCH_DATE}>{text(PlaceholderKey.MATCH_DATE)}</Hot>
        <Hot k={PlaceholderKey.MATCH_TIME}>{text(PlaceholderKey.MATCH_TIME)}</Hot>
        <Hot k={PlaceholderKey.MATCH_VENUE}>{text(PlaceholderKey.MATCH_VENUE)}</Hot>
      </Center>
    </Poster>
  );
};

export default MatchScheme;

const Center = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
`;

const Match = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: 8px;
`;

const Team = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-width: 0;
  text-align: center;
`;

const TeamName = styled.span`
  font-size: 13px;
  font-weight: 700;
`;

const Score = styled.span`
  padding: 0 6px;
  font-size: 26px;
  font-weight: 800;
`;

const Goals = styled.div`
  display: flex;
  justify-content: flex-start;
  min-height: 0;

  &:empty {
    display: none;
  }
`;
